import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import cardWatermark from "../../assets/insight-card-watermark.png";
import { TagsCountIndicator } from "../RegulationDetails/SummarySection/TagsCountIndicator";
import type { RegulationRow } from "../../services/regulationsService";
import {
  addRegulationFavorite,
  removeRegulationFavorite,
} from "../../services/regulationsService";
import {
  buildRegulationDetailHref,
  buildRegulationDetailPath,
} from "../../utils/externalNavigation";
import { useSnackbar } from "../../contexts/SnackbarContext";

export type RegulationInsightCardProps = Readonly<{
  row: RegulationRow;
  selected: boolean;
  onToggleSelected: (regulationId: string, selected: boolean) => void;
  onFavoriteChange?: (regulationId: string, isFavorite: boolean) => void;
}>;

const clampSx = (lines: number) =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }) as const;

function stopCardNavigation(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export function RegulationInsightCard({
  row,
  selected,
  onToggleSelected,
  onFavoriteChange,
}: RegulationInsightCardProps) {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);
  const regulationId = row.regulation_id || row.id;
  const title = row.title?.trim() || regulationId;
  const synopsis = row.synopsis?.trim() ?? "";
  const detailsPath = buildRegulationDetailPath(row.id);
  const detailsHref = buildRegulationDetailHref(row.id);
  const isFavorite = Boolean(row.isFavorite);

  const openDetails = () => {
    navigate(detailsPath);
  };

  const handleFavorite = async (event: React.MouseEvent) => {
    stopCardNavigation(event);
    if (favoriteSubmitting || !onFavoriteChange) return;
    const nextFavorite = !isFavorite;
    setFavoriteSubmitting(true);
    try {
      if (nextFavorite) {
        await addRegulationFavorite(row.id);
      } else {
        await removeRegulationFavorite(row.id);
      }
      onFavoriteChange(row.id, nextFavorite);
      showMessage(
        nextFavorite ? "Added to favorites" : "Removed from favorites",
        "success",
      );
    } catch (err) {
      showMessage(
        err instanceof Error ? err.message : "Failed to update favorite",
        "error",
      );
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="regulation-insight-card"
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter") {
          event.preventDefault();
          openDetails();
        }
      }}
      tabIndex={0}
      aria-label={`Open ${title}`}
      sx={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        p: "20px",
        borderRadius: "14px",
        border: "1px solid",
        borderColor: selected ? "#295EF1" : "transparent",
        background: "linear-gradient(135deg, #FFFFFF 0%, #F7F9FD 100%)",
        boxShadow: "0 2px 10px rgba(148,163,184,0.16)",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 16px rgba(25,37,50,0.12)",
        },
      }}
    >
      <Box
        component="img"
        src={cardWatermark}
        alt=""
        sx={{
          position: "absolute",
          right: "-31px",
          bottom: "-39px",
          width: 150,
          height: "auto",
          opacity: 0.6,
          transform: "rotate(14deg)",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", position: "relative" }}>
        <Box
          onClick={stopCardNavigation}
          onKeyDown={stopCardNavigation}
          sx={{ flex: "none", display: "flex", alignItems: "center", height: 22 }}
        >
          <Checkbox
            size="small"
            checked={selected}
            slotProps={{ input: { "aria-label": `Select ${regulationId}` } }}
            onChange={(_event, checked) => onToggleSelected(row.id, checked)}
            onClick={stopCardNavigation}
            sx={{ p: 0, color: "#D9D9D9", "&.Mui-checked": { color: "#295EF1" } }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={title} placement="top">
            <Box
              component="div"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#101828",
                lineHeight: 1.4,
                ...clampSx(2),
              }}
            >
              {title}
            </Box>
          </Tooltip>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "6px", flexWrap: "wrap" }}>
            <Link
              href={detailsHref}
              underline="hover"
              onClick={(event) => {
                event.preventDefault();
                stopCardNavigation(event);
                openDetails();
              }}
              sx={{ color: "#295EF1", fontSize: "13px", fontWeight: 500 }}
            >
              {regulationId}
            </Link>
            <Box
              component="span"
              sx={{
                bgcolor: "#F1F2F4",
                color: "#6A7282",
                borderRadius: "999px",
                px: 1,
                fontSize: "11px",
                lineHeight: "18px",
              }}
            >
              V {row.version}
            </Box>
            {row.isNew ? (
              <Box
                component="span"
                sx={{
                  bgcolor: "#007B4F",
                  color: "#fff",
                  borderRadius: "999px",
                  px: 1,
                  fontSize: "11px",
                  fontWeight: 600,
                  lineHeight: "18px",
                }}
              >
                NEW
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          onClick={stopCardNavigation}
          onKeyDown={stopCardNavigation}
          sx={{ display: "flex", alignItems: "center", gap: "10px", flex: "none" }}
        >
          <TagsCountIndicator
            groups={row.tagGroups ?? []}
            categoryItems={row.categoryItems}
            regulationId={regulationId}
            regulationTitle={row.title}
            showZero
            testId={`card-tags-${row.id}`}
          />
          <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
            <IconButton
              size="small"
              aria-label={isFavorite ? "remove from favorites" : "add to favorites"}
              disabled={favoriteSubmitting || !onFavoriteChange}
              onClick={(event) => void handleFavorite(event)}
              sx={{ color: isFavorite ? "#f59e0b" : "#6A7282", p: 0.5 }}
            >
              {isFavorite ? (
                <StarIcon sx={{ fontSize: 18 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            aria-label="more actions"
            onClick={(event) => {
              stopCardNavigation(event);
              setMenuAnchor(event.currentTarget);
            }}
            sx={{ color: "#6A7282", width: 24, height: 24 }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={stopCardNavigation}
          >
            <MenuItem
              onClick={(event) => {
                stopCardNavigation(event);
                setMenuAnchor(null);
                openDetails();
              }}
            >
              <ListItemIcon>
                <VisibilityOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View" />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {synopsis ? (
        <Tooltip title={synopsis} placement="bottom-start">
          <Box
            sx={{
              position: "relative",
              pl: "28px",
              pr: "60px",
              fontSize: "13px",
              color: "#8A97A9",
              lineHeight: 1.7,
              ...clampSx(3),
            }}
          >
            {synopsis}
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}
